<script setup lang="ts">
import { AlertTriangle, HelpCircle, X } from '@lucide/vue'
import { computed, nextTick, ref, watch } from 'vue'

import { useAppDialogHost } from '@/shared/dialogs/app-dialog'

const { dialog, confirm, cancel, submitPrompt } = useAppDialogHost()

const inputValue = ref('')
const inputRef = ref<HTMLInputElement | null>(null)
const confirmButtonRef = ref<HTMLButtonElement | null>(null)
const cancelButtonRef = ref<HTMLButtonElement | null>(null)

const titleId = computed(() => dialog.value ? `app-dialog-title-${dialog.value.id}` : undefined)
const messageId = computed(() => dialog.value?.message ? `app-dialog-message-${dialog.value.id}` : undefined)
const isDanger = computed(() => dialog.value?.tone === 'danger')

watch(
  dialog,
  async (current) => {
    if (!current) return
    inputValue.value = current.kind === 'prompt' ? current.initialValue : ''
    await nextTick()
    if (current.kind === 'prompt') {
      inputRef.value?.focus()
      inputRef.value?.select()
      return
    }
    if (current.tone === 'danger') {
      cancelButtonRef.value?.focus()
      return
    }
    confirmButtonRef.value?.focus()
  },
  { flush: 'post' },
)

function submit(): void {
  const current = dialog.value
  if (!current) return
  if (current.kind === 'prompt') {
    submitPrompt(inputValue.value)
    return
  }
  confirm()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="app-dialog">
      <div
        v-if="dialog"
        class="app-dialog"
        @click.self="cancel"
        @keydown.esc="cancel"
      >
        <form
          class="app-dialog__panel panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="messageId"
          @submit.prevent="submit"
        >
          <header class="app-dialog__header">
            <span
              class="app-dialog__icon"
              :class="{ 'app-dialog__icon--danger': isDanger }"
              aria-hidden="true"
            >
              <AlertTriangle v-if="isDanger" :size="20" />
              <HelpCircle v-else :size="20" />
            </span>
            <div>
              <h2 :id="titleId">{{ dialog.title }}</h2>
              <p v-if="dialog.message" :id="messageId">{{ dialog.message }}</p>
            </div>
            <button
              class="icon-button app-dialog__close"
              type="button"
              aria-label="关闭弹框"
              @click="cancel"
            >
              <X :size="19" aria-hidden="true" />
            </button>
          </header>

          <label v-if="dialog.kind === 'prompt'" class="field">
            <span class="field__label">{{ dialog.label }}</span>
            <input
              ref="inputRef"
              v-model="inputValue"
              class="input"
              :maxlength="dialog.maxLength ?? undefined"
              :placeholder="dialog.placeholder"
            />
          </label>

          <footer class="app-dialog__actions">
            <button
              ref="cancelButtonRef"
              class="button button--secondary"
              type="button"
              @click="cancel"
            >
              {{ dialog.cancelText }}
            </button>
            <button
              ref="confirmButtonRef"
              class="button"
              :class="isDanger ? 'button--danger' : 'button--primary'"
              type="submit"
            >
              {{ dialog.confirmText }}
            </button>
          </footer>
        </form>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.app-dialog {
  position: fixed;
  inset: 0;
  z-index: var(--z-toast);
  display: grid;
  padding: var(--space-4);
  place-items: center;
  background: var(--color-overlay);
}

.app-dialog__panel {
  display: grid;
  width: min(28rem, 100%);
  gap: var(--space-5);
  padding: var(--space-5);
  box-shadow: var(--shadow-lg);
}

.app-dialog__header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: flex-start;
  gap: var(--space-3);
}

.app-dialog__header h2,
.app-dialog__header p {
  margin: 0;
}

.app-dialog__header h2 {
  font-size: var(--font-size-lg);
}

.app-dialog__header p {
  margin-top: var(--space-2);
  color: var(--color-text-secondary);
}

.app-dialog__icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: var(--radius-md);
  background: var(--color-surface-brand);
  color: var(--color-primary);
}

.app-dialog__icon--danger {
  background: var(--color-danger-surface);
  color: var(--color-danger);
}

.app-dialog__close {
  width: 2.25rem;
  min-width: 2.25rem;
  height: 2.25rem;
}

.app-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.app-dialog-enter-active,
.app-dialog-leave-active {
  transition: opacity var(--motion-fast) var(--ease-standard);
}

.app-dialog-enter-active .app-dialog__panel,
.app-dialog-leave-active .app-dialog__panel {
  transition:
    opacity var(--motion-fast) var(--ease-standard),
    transform var(--motion-fast) var(--ease-standard);
}

.app-dialog-enter-from,
.app-dialog-leave-to {
  opacity: 0;
}

.app-dialog-enter-from .app-dialog__panel,
.app-dialog-leave-to .app-dialog__panel {
  opacity: 0;
  transform: translateY(var(--space-2));
}

@media (max-width: 30rem) {
  .app-dialog {
    padding: var(--space-2);
    place-items: end center;
  }

  .app-dialog__panel {
    padding: var(--space-4);
  }

  .app-dialog__header {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .app-dialog__icon {
    display: none;
  }

  .app-dialog__actions {
    flex-direction: column-reverse;
  }

  .app-dialog__actions .button {
    width: 100%;
  }
}
</style>
